import { useCallback, useState } from "react"

import styles from "./AccountingGroupModal.module.scss"
import AccountingGroupForm from "./form/AccountingGroupForm"

import { graphql } from "@/company_admin/gql/__generated__"
import type { AccountingGroupFormData } from "@/company_admin/utils/forms/accountingGroup"
import ModalDialog from "@/main/components/ui/ModalDialog/ModalDialog"
import { useCompanyAdminMutation } from "@/provider/apollo/Apollo"

const CreateAccountingGroup = graphql(/* GraphQL */ `
	mutation CreateAccountingGroup($input: CreateAccountingGroupInput!) {
		createAccountingGroup(input: $input) {
			accountingGroup {
				id
			}
		}
	}
`)

type Props = {
	onCreated: (newGroupId: string) => void
	refetchGroups: () => void
}

export const useCreateAccountingGroupModal = ({
	onCreated,
	refetchGroups,
}: Props) => {
	const [isOpen, setIsOpen] = useState(false)
	const [createAccountingGroup, { loading }] = useCompanyAdminMutation(
		CreateAccountingGroup
	)

	const handleSubmit = useCallback(
		async (data: AccountingGroupFormData) => {
			const result = await createAccountingGroup({
				variables: {
					input: {
						name: data.name,
						displayName: data.displayName,
						bankCode: data.bankCode,
						bankBranchCode: data.bankBranchCode,
						bankAccountType: data.bankAccountType!,
						bankAccountNumber: data.bankAccountNumber,
						bankAccountHolderName: data.bankAccountHolderName,
						billingEmails: data.billingEmails as string[],
					},
				},
			})
			const newId = result.data?.createAccountingGroup?.accountingGroup?.id
			if (newId) {
				refetchGroups()
				onCreated(newId)
				setIsOpen(false)
			}
		},
		[createAccountingGroup, refetchGroups, onCreated]
	)

	const render = useCallback(() => {
		return (
			<ModalDialog isOpen={isOpen} onOpenChange={setIsOpen}>
				<div className={styles.modal_content}>
					<h2 className={styles.modal_title}>経理グループを作成</h2>
					<AccountingGroupForm
						onSubmit={handleSubmit}
						onCancel={() => setIsOpen(false)}
						submitLabel="作成"
						isSubmitting={loading}
					/>
				</div>
			</ModalDialog>
		)
	}, [isOpen, handleSubmit, loading])

	return {
		render,
		openModal: () => setIsOpen(true),
	}
}
